/** A time-series chart's zoom inside its time window (TimeSeriesChart's
 *  Reset zoom bar, and the time window's Live badge). */

export interface ZoomPercent { start: number; end: number }

export interface ZoomEvent {
  type?: string
  start?: number
  end?: number
  batch?: { start?: number; end?: number }[]
}

/** The zoom an ECharts `datazoom` event reports, read off the event itself:
 *  the slider sends `start` and `end`, the wheel and drag send them in a
 *  `batch`. Reading `getOption()` instead deep-copies every series' data on
 *  each step of a wheel. */
export function zoomPercent(event: ZoomEvent): ZoomPercent {
  const item = event.batch?.[0] ?? event
  return { start: item.start ?? 0, end: item.end ?? 100 }
}

/** The earliest and latest reading across every series, as epoch ms. A
 *  loop, not Math.min(...times): a chart can hold more points than a call's
 *  argument list. Empty is [Infinity, -Infinity]. */
export function dataExtent(series: { points: readonly (readonly [string, ...unknown[]])[] }[]): [number, number] {
  let lo = Infinity
  let hi = -Infinity
  for (const s of series) {
    for (const p of s.points) {
      const t = Date.parse(p[0])
      if (t < lo) lo = t
      if (t > hi) hi = t
    }
  }
  return [lo, hi]
}

/** The stretch of time a zoom shows: the extent cut at its percentages, or
 *  null when the chart is not zoomed or has no readings. */
export function zoomWindow([lo, hi]: [number, number], { start, end }: ZoomPercent): [Date, Date] | null {
  if (start <= 0 && end >= 100) return null
  if (lo > hi) return null
  return [new Date(lo + ((hi - lo) * start) / 100), new Date(lo + ((hi - lo) * end) / 100)]
}

/** The set of zoomed charts after one reports, for a page of several (the
 *  gateway instrument page): the time window is zoomed while any chart is.
 *  The same set back when nothing changed, so a state setter re-renders
 *  nothing. */
export function zoomedCharts(zoomed: ReadonlySet<string>, key: string, isZoomed: boolean): Set<string> {
  if (zoomed.has(key) === isZoomed) return zoomed as Set<string>
  const next = new Set(zoomed)
  if (isZoomed) next.add(key)
  else next.delete(key)
  return next
}

interface SeriesLike {
  station: string; parameter: string; display_name: string
  unit: string; axis_group: string; kind: string; points: unknown
}

/** Whether two series lists draw the same chart: the same readings (the
 *  same `points` array, which only a new query replaces) under the same
 *  names, units and axes. The gateway page rebuilds its series wrappers on
 *  every render (`chartGroups`), its 30-second sensor poll included, and a
 *  chart that took each rebuild as new data reset the reader's zoom. */
export function sameSeries(a: readonly SeriesLike[], b: readonly SeriesLike[]): boolean {
  return a.length === b.length && a.every((x, i) => {
    const y = b[i]!
    return x.points === y.points && x.station === y.station && x.parameter === y.parameter
      && x.display_name === y.display_name && x.unit === y.unit
      && x.axis_group === y.axis_group && x.kind === y.kind
  })
}
